/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
import { PropsWithChildren, useCallback, useContext, useMemo } from "react";

import { CSSProperties, css } from "@compiled/react";
import { defineMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import { isLocalizableHref, useLocalizedHref } from "@/libraries/web-framework";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import {
    LogoV2 as Logo,
    LogoBrandV2 as LogoBrand,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import { MotionContainer } from "./components/motion-container";
import { GlobalNavProductRecommendationsExperiment } from "./global-nav-product-recommendations-experiment";
import { NavProductRecommendationsContext } from "./global-nav-product-recommendations-experiment/context/nav-product-recommendations-context";
import { SubnavWithSidebarTabs } from "./layouts/subnav-with-sidebar";
import { useCurrentNav, DivOnHoverExit } from "./nav-links-container";

export enum ProductCategory {
    Featured,
    Developers,
    ProductManagers,
    ITProfessionals,
    BusinessTeams,
    LeadershipTeams
}

export enum Treatment {
    New
}

type Category = {
    id: ProductCategory;
    title: ReturnType<typeof defineMessage>;
};

const categories = {
    [ProductCategory.Featured]: {
        id: ProductCategory.Featured,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.everyone",
            defaultMessage: "Featured"
        })
    },
    [ProductCategory.Developers]: {
        id: ProductCategory.Developers,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.developers",
            defaultMessage: "Developers"
        })
    },
    [ProductCategory.ProductManagers]: {
        id: ProductCategory.ProductManagers,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.product-managers",
            defaultMessage: "Product Managers"
        })
    },
    [ProductCategory.ITProfessionals]: {
        id: ProductCategory.ITProfessionals,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.it-professionals",
            defaultMessage: "IT professionals"
        })
    },
    [ProductCategory.BusinessTeams]: {
        id: ProductCategory.BusinessTeams,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.business-teams",
            defaultMessage: "Business Teams"
        })
    },
    [ProductCategory.LeadershipTeams]: {
        id: ProductCategory.LeadershipTeams,
        title: defineMessage({
            id: "globalnav.v2.products.tabs.leadership-teams",
            defaultMessage: "Leadership Teams"
        })
    }
} as const satisfies Record<ProductCategory, Category>;

export type Product = {
    id: string;
    name: string;
    description: ReturnType<typeof defineMessage>;
    tags: ReadonlyArray<ProductCategory>;
    logo: LogoBrand;
    href: string;
    treatment?: Treatment;
};

export type ProductsByTag = Record<ProductCategory, Array<Product>>;

export function useProductData() {
    const { locale } = useIntl();

    // we could move this to top of the module, but expecting this structure will
    // be derived from dynamic data at some point
    const products = useMemo(() => {
        return {
            jira: {
                id: "jira",
                name: "Jira",
                description: defineMessage({
                    id: "globalnav.v2.products.jira.description",
                    defaultMessage: "Flexible project management"
                }),
                href: "/software/jira",
                tags: [
                    ProductCategory.Featured,
                    ProductCategory.Developers,
                    ProductCategory.ProductManagers,
                    ProductCategory.BusinessTeams,
                    ProductCategory.LeadershipTeams
                ],
                logo: "jira"
            },
            confluence: {
                id: "confluence",
                name: "Confluence",
                description: defineMessage({
                    id: "globalnav.v2.products.confluence.description",
                    defaultMessage: "Knowledge, all in one place"
                }),
                href: "/software/confluence",
                tags: [
                    ProductCategory.Featured,
                    ProductCategory.ProductManagers,
                    ProductCategory.BusinessTeams,
                    ProductCategory.LeadershipTeams
                ],
                logo: "confluence"
            },
            jsm: {
                id: "jsm",
                name: "Jira Service Management",
                description: defineMessage({
                    id: "globalnav.v2.products.jsm.description",
                    defaultMessage: "High-velocity service delivery"
                }),
                href: "/software/jira/service-management",
                tags: [
                    ProductCategory.Featured,
                    ProductCategory.ITProfessionals
                ],
                logo: "jiraservicemanagement"
            },
            rovo: {
                id: "rovo",
                name: "Rovo",
                description: defineMessage({
                    id: "globalnav.v2.products.rovo.description",
                    defaultMessage: "Unlock enterprise knowledge"
                }),
                href: `/software/rovo`,
                tags: [ProductCategory.Featured],
                logo: "rovo",
                treatment: Treatment.New
            },
            jpd: {
                id: "jpd",
                name: "Jira Product Discovery",
                description: defineMessage({
                    id: "globalnav.v2.products.jpd.description",
                    defaultMessage: "Capture & prioritize ideas"
                }),
                href: "/software/jira/product-discovery",
                tags: [
                    ProductCategory.Featured,
                    ProductCategory.ProductManagers
                ],
                logo: "jiraproductdiscovery",
                treatment: Treatment.New
            },
            compass: {
                id: "compass",
                name: "Compass",
                description: defineMessage({
                    id: "globalnav.v2.products.compass.description",
                    defaultMessage: "Optimize software health"
                }),
                href: "/software/compass",
                tags: [ProductCategory.Featured, ProductCategory.Developers],
                logo: "compass",
                treatment: Treatment.New
            },
            guard: {
                id: "guard",
                name: "Guard",
                description: defineMessage({
                    id: "globalnav.v2.products.guard.description",
                    defaultMessage: "Enhanced cloud security"
                }),
                href: "/software/access",
                tags: [
                    ProductCategory.Featured,
                    ProductCategory.ITProfessionals
                ],
                logo: "guard",
                treatment: Treatment.New
            },
            loom: {
                id: "loom",
                name: "Loom",
                description: defineMessage({
                    id: "globalnav.v2.products.loom.description",
                    defaultMessage: "Quick, async video updates"
                }),
                href: "//loom.com",
                tags: [
                    ProductCategory.Featured,
                    ProductCategory.BusinessTeams,
                    ProductCategory.LeadershipTeams
                ],
                logo: "loom",
                treatment: Treatment.New
            },
            bitbucket: {
                id: "bitbucket",
                name: "Bitbucket",
                description: defineMessage({
                    id: "globalnav.v2.products.bitbucket.description",
                    defaultMessage: "Collaborative code repos"
                }),
                href: "/software/bitbucket",
                tags: [ProductCategory.Developers],
                logo: "bitbucket"
            },
            jiraalign: {
                id: "jiraalign",
                name: "Jira Align",
                description: defineMessage({
                    id: "globalnav.v2.products.jiraalign.description",
                    defaultMessage: "Enterprise strategy alignment"
                }),
                href: "/software/jira/align",
                tags: [ProductCategory.LeadershipTeams],
                logo: "jiraalign",
                treatment: Treatment.New
            }
        } as const satisfies Record<string, Product>;
    }, [locale]);

    const productsByTag = useMemo(
        () =>
            Object.values(products).reduce<ProductsByTag>(
                (acc, product) => {
                    product.tags.forEach((tag) => {
                        acc[tag] = acc[tag].concat(product);
                    });

                    return acc;
                },
                {
                    [ProductCategory.Featured]: [],
                    [ProductCategory.Developers]: [],
                    [ProductCategory.ProductManagers]: [],
                    [ProductCategory.ITProfessionals]: [],
                    [ProductCategory.BusinessTeams]: [],
                    [ProductCategory.LeadershipTeams]: []
                }
            ),
        [products]
    );

    return {
        products,
        productsByTag
    };
}

const Pill = ({
    color = "#09326C",
    backgroundColor = "#8FB8F6",
    children
}: PropsWithChildren<Pick<CSSProperties, "color" | "backgroundColor">>) => {
    return (
        <div
            css={css({
                display: "flex",
                alignItems: "center",
                paddingInline: "6px",
                paddingBlock: "2px",
                borderRadius: "24px"
            })}
            style={{
                color,
                backgroundColor
            }}
        >
            {children}
        </div>
    );
};

const treatments = {
    [Treatment.New]: (
        <Pill>
            <span
                css={css({
                    fontSize: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase"
                })}
            >
                New
            </span>
        </Pill>
    )
};

export const ProductItem = ({
    id,
    logo,
    name,
    treatment,
    description,
    href
}: Product) => {
    const intl = useIntl();
    const localizedHref = useLocalizedHref(href);
    const formattedHref = isLocalizableHref(href) ? localizedHref : href;

    return (
        <div
            css={css({
                display: "flex",
                paddingBlockStart: token("space.300"),
                paddingBlockEnd: token("space.300"),
                width: "17.5rem",

                [media.above.sm]: {
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    h4: {
                        display: "flex",
                        flexWrap: "nowrap",
                        margin: 0,
                        fontSize: "14px",
                        lineHeight: "normal",
                        fontWeight: 700,
                        color: "#101214"
                    },

                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    p: {
                        margin: 0,
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#505258",
                        lineHeight: "normal"
                    }
                }
            })}
        >
            <a
                css={css({
                    "display": "flex",
                    "alignItems": "center",
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "&:hover h4": {
                        color: "#1868DB"
                    }
                })}
                href={formattedHref}
                target={isLocalizableHref(href) ? "" : "_blank"}
                rel={isLocalizableHref(href) ? "" : "noreferrer"}
                aria-label={name}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="globalNavSubnav"
                data-product-key={id}
            >
                <div
                    css={css({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginInlineEnd: token("space.150"),
                        height: "44px",
                        width: "44px",
                        backgroundColor: "#F0F1F2",
                        borderRadius: "4px",
                        alignSelf: "baseline"
                    })}
                >
                    <Logo
                        brand={logo}
                        type="mark"
                        appearance="brand"
                        width={32}
                        height={32}
                    />
                </div>
                <div
                    css={css({
                        width: "224px"
                    })}
                >
                    <h4>
                        <span css={css({ marginInlineEnd: "6px" })}>
                            {name}
                        </span>
                        {typeof treatment !== "undefined"
                            ? treatments[treatment]
                            : null}
                    </h4>
                    <p>{intl.formatMessage(description)}</p>
                </div>
            </a>
        </div>
    );
};

export const ProductGrid = ({ children }: PropsWithChildren) => {
    return (
        <div
            css={css({
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: token("space.400"),
                paddingInline: `${token("space.400")} ${token("space.500")}`,
                paddingBlock: token("space.500")
            })}
        >
            {children}
        </div>
    );
};

export const ProductSubnav = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();
    const { productsByTag } = useProductData();

    const { direction } = useCurrentNav();

    // Trailblazers experiment TBLZ-76 start
    const {
        shouldShowGlobalNavProductRecommendations,
        fireGlobalNavProductRecoEventsWhenInView
    } = useContext(NavProductRecommendationsContext);

    const featuredProductIndex = useMemo(
        () =>
            Object.values(categories).findIndex(
                (category) => category.id === ProductCategory.Featured
            ),
        []
    );
    const onTabInView = useCallback(
        (index: number) => {
            if (featuredProductIndex === index) {
                fireGlobalNavProductRecoEventsWhenInView({
                    isMobile: false
                });
            }
        },
        [featuredProductIndex, fireGlobalNavProductRecoEventsWhenInView]
    );
    // Trailblazers experiment TBLZ-76 end

    return (
        <DivOnHoverExit>
            <TypographyStyles>
                <MotionContainer direction={direction}>
                    <SubnavWithSidebarTabs
                        tabs={Object.values(categories).map((category) => {
                            return {
                                id: category.id.toString(),
                                title: intl.formatMessage(category.title),
                                content:
                                    category.id === ProductCategory.Featured &&
                                    shouldShowGlobalNavProductRecommendations ? (
                                        <GlobalNavProductRecommendationsExperiment />
                                    ) : (
                                        <ProductGrid>
                                            {productsByTag[category.id].map(
                                                (product) => (
                                                    <ProductItem
                                                        key={product.id}
                                                        {...product}
                                                    />
                                                )
                                            )}
                                        </ProductGrid>
                                    )
                            };
                        })}
                        onTabInView={onTabInView}
                        links={
                            <div
                                css={css({
                                    gridArea: "tablist",
                                    display: "flex",
                                    alignItems: "center",
                                    height: "max-content",
                                    paddingInlineStart: token("space.500"),
                                    paddingInlineEnd: token("space.200")
                                })}
                            >
                                <div
                                    css={css({
                                        paddingInlineStart: token("space.200"),

                                        a: {
                                            fontSize: "14px"
                                        }
                                    })}
                                >
                                    <Link
                                        href={useLocalizedHref("/software")}
                                        text={intl.formatMessage({
                                            id: "globalnav.v2.subnav.tabs.see-all-products",
                                            defaultMessage: "See all products"
                                        })}
                                        ariaLabel={intl.formatMessage({
                                            id: "globalnav.v2.subnav.tabs.see-all-products-arialabel",
                                            defaultMessage:
                                                "See all products link"
                                        })}
                                        dataEventProps={{
                                            "data-product-key": productKey,
                                            "data-event-component":
                                                "linkButton",
                                            "data-event-container":
                                                "navbarDropdown",
                                            "data-label": "See all products",
                                            "data-event": "clicked"
                                        }}
                                        type="basic"
                                        doesArrowAnimate={true}
                                    />
                                </div>
                            </div>
                        }
                    />
                </MotionContainer>
            </TypographyStyles>
        </DivOnHoverExit>
    );
};
